import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import {
  Badge, Button, Dot, EmptyState, ErrorState, Grid, Input, KindInput,
  PageHeader, Panel, Row, SegmentedControl, Skeleton, Stack, type InputKind, type InputValue,
} from "@akaike/ui";
import {
  getListSettingsV1SettingsGetQueryKey,
  useDeleteSettingV1SettingsKeyDelete,
  useListSettingsV1SettingsGet,
  usePutSettingV1SettingsKeyPut,
} from "../api/generated/qcsControlPlane";
import { SettingSource, SettingValidity, type SettingOut } from "../api/generated/model";
import { ApiError } from "../api/mutator";
import { platformColor, platformLabel } from "../api/platforms";
import { useCurrentUser } from "../auth/me";

function inputKind(kind: SettingOut["kind"]): InputKind {
  switch (kind.kind) {
    case "int":
      return { kind: "int", min: kind.min, max: kind.max, unit: kind.unit };
    case "float":
      return { kind: "float", min: kind.min, max: kind.max, min_exclusive: kind.min_exclusive, max_exclusive: kind.max_exclusive, unit: kind.unit };
    case "bool":
      return { kind: "bool" };
    case "string":
      return { kind: "string" };
    case "enum":
      return { kind: "enum", options: kind.options };
    case "enum_set":
      return { kind: "enum_set", options: kind.options };
    case "by_platform":
      return { kind: "by_platform", fields: Object.fromEntries(Object.entries(kind.fields).map(([name, field]) => [name, inputKind(field)])) };
    case undefined:
      throw new Error("Setting kind is missing");
  }
}

function inputValue(value: unknown, kind: InputKind): InputValue {
  switch (kind.kind) {
    case "int":
    case "float":
      if (typeof value === "number" || value === null) return value;
      throw new Error("Expected a numeric setting value");
    case "bool":
      if (typeof value === "boolean") return value;
      throw new Error("Expected a boolean setting value");
    case "string":
    case "enum":
      if (typeof value === "string") return value;
      throw new Error("Expected a string setting value");
    case "enum_set":
      if (Array.isArray(value) && value.every((item) => typeof item === "string")) return value;
      throw new Error("Expected a list of setting values");
    case "by_platform": {
      if (value === null || typeof value !== "object" || Array.isArray(value)) throw new Error("Expected platform settings");
      return Object.fromEntries(Object.entries(kind.fields).map(([platform, field]) => {
        const member = Object.entries(value).find(([key]) => key === platform)?.[1] ?? null;
        return [platform, member === null ? null : inputValue(member, field)];
      }));
    }
  }
}

function recordValue(value: InputValue): { [key: string]: InputValue } {
  if (value === null || typeof value !== "object" || Array.isArray(value)) throw new Error("Expected platform settings");
  return value;
}

function displayValue(value: unknown): string {
  if (typeof value === "string") return value === "" ? "(empty string)" : value;
  if (value === null) return "none";
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
}

function sourceLabel(source: SettingSource): string {
  switch (source) {
    case SettingSource.code: return "code";
    case SettingSource.env: return "env";
    case SettingSource.db: return "db";
  }
}

function sourceNote(setting: SettingOut): string {
  if (setting.version === null) return setting.env_value === null ? "no override" : `no override; env sets ${displayValue(setting.env_value)}`;
  if (setting.env_value !== null) return `default ${displayValue(setting.env_value)} (env; overrides code)`;
  return `default ${displayValue(setting.code_default)} (code)`;
}

function settingField(setting: SettingOut): string {
  return setting.key.split(".").at(-1) ?? setting.key;
}

function sectionLabel(section: string): string {
  const label = section.replaceAll("_", " ");
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function platformDefault(setting: SettingOut, platform: string): string {
  const baseline = setting.env_value === null ? setting.code_default : setting.env_value;
  if (baseline === null || typeof baseline !== "object" || Array.isArray(baseline)) throw new Error("Expected platform defaults");
  const value = Object.entries(baseline).find(([name]) => name === platform)?.[1];
  return value === undefined || value === null ? "default: inherits" : `default ${displayValue(value)} (${setting.env_value === null ? "code" : "env"})`;
}

type PlatformField = { platform: string; kind: InputKind; value: InputValue; baseline: string };

function PlatformFields({ setting, kind, value, disabled, onChange }: {
  setting: SettingOut;
  kind: Extract<InputKind, { kind: "by_platform" }>;
  value: InputValue;
  disabled: boolean;
  onChange: (value: InputValue) => void;
}) {
  const platforms = recordValue(value);
  const fields: PlatformField[] = Object.entries(kind.fields).map(([platform, member]) => ({
    platform, kind: member, value: platforms[platform], baseline: platformDefault(setting, platform),
  }));
  const enumFields = fields.filter((field) => field.kind.kind === "enum_set");
  const scalarFields = fields.filter((field) => field.kind.kind !== "enum_set");
  const enumOptions = Array.from(new Set(enumFields.flatMap((field) => field.kind.kind === "enum_set" ? field.kind.options : [])));

  function change(platform: string, next: InputValue) {
    onChange({ ...platforms, [platform]: next });
  }

  return (
    <Stack gap="md" className="min-w-0">
      {scalarFields.length > 0 && <Grid min={170} gap="sm" className="min-w-0">
        {scalarFields.map((field) => (
          <Panel key={field.platform} className="min-w-0">
            <Panel.Body>
              <Stack gap="sm">
                <label htmlFor={`${setting.key}-${field.platform}`} className="flex items-center gap-2 font-mono text-xs font-semibold text-foreground">
                  <Dot style={{ backgroundColor: platformColor(field.platform) }} aria-hidden="true" />
                  {platformLabel(field.platform)}
                </label>
                <KindInput id={`${setting.key}-${field.platform}`} kind={field.kind} value={field.value} disabled={disabled} compact aria-label={`${platformLabel(field.platform)} ${setting.title}`} onChange={(next) => change(field.platform, next)} />
                <p className="font-mono text-[10px] text-muted-foreground">{field.baseline}</p>
              </Stack>
            </Panel.Body>
          </Panel>
        ))}
      </Grid>}
      {enumFields.length > 0 && <Panel className="min-w-0"><Panel.Body className="overflow-x-auto">
        <table className="w-full border-collapse text-xs">
          <thead><tr><th scope="col" className="p-1 text-left font-normal text-muted-foreground">Platform</th>{enumOptions.map((option) => <th scope="col" key={option} className="p-1 text-center font-normal text-muted-foreground">{option}</th>)}</tr></thead>
          <tbody>{enumFields.map((field) => {
            const options = field.kind.kind === "enum_set" ? field.kind.options : [];
            return (
              <tr key={field.platform} className="border-t border-border">
                <th scope="row" className="p-1 text-left font-medium"><span className="flex items-center gap-2"><Dot style={{ backgroundColor: platformColor(field.platform) }} aria-hidden="true" />{platformLabel(field.platform)}</span></th>
                {enumOptions.map((option) => {
                  const supported = options.includes(option);
                  return <td key={option} className="p-1 text-center"><input type="checkbox" aria-label={`${field.platform} ${option}`} checked={Array.isArray(field.value) && field.value.includes(option)} disabled={disabled || !supported} onChange={(event) => {
                    const selected = Array.isArray(field.value) ? field.value : [];
                    change(field.platform, event.currentTarget.checked ? [...selected, option] : selected.filter((item) => item !== option));
                  }} /></td>;
                })}
              </tr>
            );
          })}</tbody>
        </table>
      </Panel.Body></Panel>}
    </Stack>
  );
}

function SettingRow({ setting, canEdit }: { setting: SettingOut; canEdit: boolean }) {
  const client = useQueryClient();
  const kind = inputKind(setting.kind);
  const [draft, setDraft] = useState<InputValue | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const save = usePutSettingV1SettingsKeyPut();
  const reset = useDeleteSettingV1SettingsKeyDelete();
  const busy = save.isPending || reset.isPending;
  const hasOverride = setting.version !== null;
  const baseline = inputValue(setting.effective_value, kind);
  const value = draft === undefined ? baseline : draft;
  const isDirty = draft !== undefined && JSON.stringify(draft) !== JSON.stringify(baseline);

  function changed(next: InputValue) {
    setDraft(next);
    setError(null);
    setNotice(null);
  }

  function succeeded(updated: SettingOut) {
    client.setQueryData<SettingOut[]>(getListSettingsV1SettingsGetQueryKey(), (rows) => rows?.map((row) => row.key === updated.key ? updated : row));
    setDraft(undefined);
    setError(null);
    setNotice(null);
    void client.invalidateQueries({ queryKey: getListSettingsV1SettingsGetQueryKey() });
  }

  function failed(failure: unknown) {
    if (failure instanceof ApiError && failure.status === 409) {
      setDraft(undefined);
      setError(null);
      setNotice("Changed by someone else. Latest value is loading; review it before saving again.");
      void client.invalidateQueries({ queryKey: getListSettingsV1SettingsGetQueryKey() });
    } else if (failure instanceof ApiError && failure.status === 422) {
      setError(failure.message);
    } else {
      setError(failure instanceof Error ? failure.message : "Could not update this setting.");
    }
  }

  function submit() {
    if (!isDirty || draft === undefined || draft === null) return;
    setError(null);
    setNotice(null);
    save.mutate({ key: setting.key, data: { value: draft, expected_version: setting.version } }, { onSuccess: succeeded, onError: failed });
  }

  function remove() {
    if (setting.version === null) return;
    setError(null);
    setNotice(null);
    reset.mutate({ key: setting.key, params: { expected_version: setting.version } }, { onSuccess: succeeded, onError: failed });
  }

  const id = `setting-${setting.key.replaceAll(".", "-")}`;
  const metadata = <>
    <Row wrap align="start" className="min-w-0">
      <span className="min-w-0 break-words text-sm font-medium text-foreground">{setting.title}</span>
      {setting.validity === SettingValidity.invalid && <Badge variant="outline-warning"><AlertTriangle className="mr-1 size-3" />Invalid override</Badge>}
    </Row>
    <p className="break-all font-mono text-[11px] text-muted-foreground" title={setting.key}>{settingField(setting)}</p>
    <p className="text-xs leading-5 text-muted-foreground">{setting.description}</p>
    {setting.validity === SettingValidity.invalid && <p role="alert" className="text-xs text-amber-700 dark:text-amber-400">{setting.error}</p>}
  </>;
  const source = <><Badge variant={setting.source === SettingSource.db ? "outline-success" : setting.source === SettingSource.env ? "outline-warning" : "outline"}>{sourceLabel(setting.source)}</Badge><span className="min-w-0 break-words font-mono text-[11px] text-muted-foreground">{sourceNote(setting)}</span></>;
  const feedback = <>{notice && <p role="status" className="text-xs text-muted-foreground">{notice}</p>}{error && <p role="alert" className="text-xs text-destructive">{error}</p>}</>;

  if (kind.kind === "by_platform") return (
    <Stack gap="md" className="min-w-0 px-3.5 py-3">
      <div className="grid min-w-0 gap-2 md:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)_7rem]">
        <div className="min-w-0 space-y-0.5">{metadata}</div>
        <div className="hidden md:block" />
        <Stack gap="sm" className="min-w-0">{source}</Stack>
        <Row align="start">{canEdit && hasOverride && <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={remove}>Reset all</Button>}</Row>
      </div>
      <PlatformFields setting={setting} kind={kind} value={value} disabled={!canEdit || busy} onChange={changed} />
      {feedback}
      {canEdit && isDirty && <Row justify="end">
        <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => changed(inputValue(setting.effective_value, kind))}>Cancel</Button>
        <Button type="button" size="sm" disabled={busy || draft === null} onClick={submit}>Save</Button>
      </Row>}
    </Stack>
  );

  return (
    <div className="grid min-w-0 gap-2 px-3.5 py-3 md:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)_7rem]">
      <div className="min-w-0 space-y-0.5">{metadata}</div>
      <div className="min-w-0 self-start">
        <KindInput id={id} kind={kind} value={value} disabled={!canEdit || busy} compact aria-label={`Effective value for ${setting.title}`} aria-invalid={Boolean(error)} onChange={changed} />
      </div>
      <Stack gap="sm" className="min-w-0">{source}</Stack>
      <Row wrap align="start" className="self-start justify-self-start">
        {canEdit && isDirty && <Button size="sm" type="button" disabled={busy || draft === null} onClick={submit}>Save</Button>}
        {canEdit && isDirty && <Button size="sm" variant="ghost" type="button" disabled={busy} onClick={() => changed(baseline)}>Cancel</Button>}
        {canEdit && hasOverride && !isDirty && <Button size="sm" variant="ghost" type="button" disabled={busy} onClick={remove}>Reset</Button>}
      </Row>
      {(notice || error || (draft === null && canEdit)) && <div className="md:col-span-4">{feedback}{draft === null && canEdit && <p className="text-xs text-muted-foreground">Enter a value or Reset the override.</p>}</div>}
    </div>
  );
}

export function Settings() {
  const { is_superuser: canEdit } = useCurrentUser();
  const query = useListSettingsV1SettingsGet();
  const [search, setSearch] = useState("");
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const settings = query.data ?? [];
  const sections = new Map<string, SettingOut[]>();
  for (const setting of settings) {
    const section = setting.section ?? "General";
    const rows = sections.get(section) ?? [];
    rows.push(setting);
    sections.set(section, rows);
  }
  const filter = search.trim().toLowerCase();
  const matchedSections = Array.from(sections, ([section, rows]) => ({
    section, rows: rows.filter((setting) => `${setting.key} ${setting.title}`.toLowerCase().includes(filter)),
    overridden: rows.filter((setting) => setting.version !== null).length,
  })).filter(({ rows }) => rows.length > 0);
  const selected = matchedSections.find(({ section }) => section === activeSection) ?? matchedSections[0];
  const overridden = settings.filter((setting) => setting.version !== null).length;

  return (
    <Stack gap="lg" className="min-w-0 w-full">
      <PageHeader className="min-w-0" title="Settings" description={`Overrides on top of env and code defaults. Job tuning lives on each job. ${overridden} overridden.`} action={
        <Input aria-label="Filter keys" className="h-8 w-full min-w-0 max-w-52 text-xs" placeholder="Filter keys" value={search} onChange={(event) => setSearch(event.currentTarget.value)} />
      } />
      {!canEdit && <p className="rounded-md border border-border bg-card px-3 py-2 text-xs text-muted-foreground">Read-only. Only superusers can change runtime settings.</p>}
      {query.isPending ? <div className="space-y-2"><Skeleton h={42} /><Skeleton h={42} /><Skeleton h={42} /></div> : query.isError ? (
        <ErrorState title="Could not load settings" message={String(query.error)} onRetry={() => void query.refetch()} />
      ) : matchedSections.length === 0 ? <EmptyState title="No matching settings">Try another key.</EmptyState> : <>
        <div className="min-w-0 max-w-full overflow-x-auto">
          <SegmentedControl label="Settings sections" value={selected.section}
            options={matchedSections.map(({ section, rows, overridden: count }) => ({
              value: section,
              label: `${sectionLabel(section)}${count > 0 ? ` (${count})` : ""}${filter ? ` - ${rows.length} matches` : ""}`,
            }))}
            onChange={setActiveSection} />
        </div>
        <Panel role="tabpanel" aria-label={`${selected.section} settings`} className="min-w-0">
          <Panel.Body tight className="divide-y divide-border">
          {selected.rows.map((setting) => <SettingRow key={setting.key} setting={setting} canEdit={canEdit} />)}
          </Panel.Body>
        </Panel>
      </>}
      <footer className="pt-3 text-[11px] text-muted-foreground">
        Icons by <a href="https://streamlinehq.com" target="_blank" rel="noreferrer" className="underline">Streamline</a> under CC BY 4.0.
      </footer>
    </Stack>
  );
}
